import { apiRequest } from "./api";

/**
 * v2.2 P1.3: a browser error reaches the same ledger as a server one.
 *
 * `window.onerror`, unhandled promise rejections and the React error boundary all post here; the API
 * stores the report in `api_errors` with status 0 and /system lists it beside the 5xx rows. Best-effort
 * by construction: a report that fails is dropped, and an identical message inside ten seconds is not
 * sent twice, so a render loop that throws every frame cannot become a request loop as well.
 */
export interface ClientErrorReport {
  message: string;
  stack?: string | null;
  route?: string | null;
  kind?: "error" | "unhandledrejection" | "boundary";
}

const DEDUPE_MS = 10_000;
const recent = new Map<string, number>();

export function shouldSend(message: string, now: number = Date.now()): boolean {
  const last = recent.get(message);
  if (last !== undefined && now - last < DEDUPE_MS) return false;
  recent.set(message, now);
  if (recent.size > 100) recent.clear();
  return true;
}

export function resetClientErrorDedupe(): void {
  recent.clear();
}

export function reportClientError(report: ClientErrorReport): void {
  const message = (report.message || "unknown error").slice(0, 4000);
  if (!shouldSend(message)) return;
  void apiRequest<{ ref: string }>("/api/v1/client-errors", {
    method: "POST",
    body: JSON.stringify({
      message,
      stack: report.stack ? report.stack.slice(0, 20_000) : null,
      route: report.route ?? window.location.pathname,
      kind: report.kind ?? "error",
    }),
  }).catch(() => undefined);
}

function describe(reason: unknown): { message: string; stack: string | null } {
  if (reason instanceof Error) return { message: `${reason.name}: ${reason.message}`, stack: reason.stack ?? null };
  if (typeof reason === "string") return { message: reason, stack: null };
  try {
    return { message: JSON.stringify(reason), stack: null };
  } catch {
    return { message: String(reason), stack: null };
  }
}

let installed = false;

/** Called once from main.tsx. */
export function installClientErrorReporting(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener("error", (event: ErrorEvent) => {
    const { message, stack } = describe(event.error ?? event.message);
    reportClientError({ message, stack, kind: "error" });
  });
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    // An ApiError is already a server-side row (or a 4xx the toast explained); reporting it again from
    // the browser would count one failure twice.
    const reason: unknown = event.reason;
    if (reason instanceof Error && reason.name === "ApiError") return;
    const { message, stack } = describe(reason);
    reportClientError({ message, stack, kind: "unhandledrejection" });
  });
}
